import React from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';

import { Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import Loader from '../../../components/layout/loader/Loader';
import { useGetMyDetailsApiQuery } from '../../../redux/api/users/usersApi';
import { Paths } from '../../../shared/paths';
import AuthLanguageSelector from '../components/auth-language-selector/AuthLanguageSelector';
import { useStyles } from './CompleteRegistration.styles';
import RegistrationSkipSettingsDialog from './RegistrationSkipSettingsDialog';
import RegistrationWizardActions from './RegistrationWizardActions';
import RegistrationWizardSteps, {
  getRegistrationStepTitle,
} from './RegistrationWizardSteps';
import { useCompleteRegistrationWizard } from './useCompleteRegistrationWizard';

function CompleteRegistration() {
  const { t } = useTranslation();
  const {
    data: me,
    isLoading: isMeLoading,
    isFetching,
    isUninitialized,
    refetch: refetchMe,
  } = useGetMyDetailsApiQuery(undefined);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { classes } = useStyles(theme);
  const {
    formik,
    step,
    isSaving,
    completionDialogOpen,
    goToNextStep,
    goToPreviousStep,
    handleSkipSetupClick,
    handleSave,
    handleCompletionDialogConfirm,
    languages,
  } = useCompleteRegistrationWizard(me, refetchMe);

  const stepTitle = getRegistrationStepTitle(step, t);

  if ((isMeLoading || isFetching || isUninitialized) && !me?.id) {
    return (
      <div>
        <Loader />
      </div>
    );
  }

  if (me?.id && me.isRegistrationCompleted !== false && !completionDialogOpen) {
    return <Navigate to={Paths.root} replace />;
  }

  return (
    <div className={classes.signUpWrapper}>
      {isMobile ? null : (
        <AuthLanguageSelector className={classes.desktopLanguageSelector} />
      )}
      <div className={classes.signUpContentWrapper}>
        {isMobile && (
          <AuthLanguageSelector className={classes.mobileLanguageSelector} />
        )}
        <form
          className={classes.wizardForm}
          onSubmit={(e) => e.preventDefault()}>
          {stepTitle ? (
            <Typography variant="h2" className={classes.stepTitle}>
              {stepTitle}
            </Typography>
          ) : null}

          <RegistrationWizardSteps
            step={step}
            formik={formik}
            languages={languages}
          />

          <RegistrationWizardActions
            step={step}
            isSaving={isSaving}
            onBack={goToPreviousStep}
            onNext={goToNextStep}
            onSkipSetup={handleSkipSetupClick}
            onSave={handleSave}
          />
        </form>
      </div>

      <RegistrationSkipSettingsDialog
        open={completionDialogOpen}
        title={t('signUp.completeRegistration.completionDialogTitle')}
        message={t('signUp.completeRegistration.completionDialogMessage')}
        onConfirm={handleCompletionDialogConfirm}
      />
    </div>
  );
}

export default CompleteRegistration;
